import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { CourtHouse } from 'lib/enums/CourtHouse';
import { MailDelivery, EAddress } from 'lib/types';
import { MailedNoticeDocumentType, ShippingTier } from 'lib/types/mail';
import { AffidavitType, ShippingSpeed } from '../DeliveryAddOns/pricingConfig';
import {
  DEFAULT_MAILING,
  DEFAULT_COURTHOUSE,
  DEFAULT_ERROR,
  getRecipientValidation,
  getCourthouseValidation,
  hasPopulatedAddress
} from './deliveryRecipients.helpers';
import type { AffidavitRecipientsFormState } from './deliveryRecipients.helpers';

export {
  DEFAULT_MAILING,
  DEFAULT_COURTHOUSE,
  DEFAULT_ERROR,
  MAX_MAIL_COPIES,
  hasPopulatedAddress,
  selectIsFormComplete
} from './deliveryRecipients.helpers';
export type { AffidavitRecipientsFormState } from './deliveryRecipients.helpers';

const MAX_COURTHOUSES = 5;
const MAX_MAIL_ADDRESSES = 5;

export const initialState: AffidavitRecipientsFormState = {
  sendAffidavitByEmail: true,
  sendAffidavitByMail: false,
  mailingRecipients: [DEFAULT_MAILING],
  recipientValidationErrors: DEFAULT_ERROR,
  sendAffidavitToCourthouse: false,
  courthouseRecipients: [DEFAULT_COURTHOUSE],
  courthouseValidationErrors: DEFAULT_ERROR,
  showErrors: false,
  affidavitType: null,
  shippingSpeed: 'ground',
  deliveryOption: 'email',
  wantsAdditionalAffidavit: false,
  noticeTypeDefaultRecipients: [],
  invoiceMailingRecipients: [],
  invoiceRecipientValidationErrors: DEFAULT_ERROR
};

export const { reducer, actions } = createSlice({
  name: 'deliveryRecipients',
  initialState,
  reducers: {
    reset: () => initialState,

    setSendAffidavitByEmail: (state, action: PayloadAction<boolean>) => {
      state.sendAffidavitByEmail = action.payload;
    },

    setSendAffidavitByMail: (state, action: PayloadAction<boolean>) => {
      state.sendAffidavitByMail = action.payload;
      if (action.payload === false) {
        state.mailingRecipients = [DEFAULT_MAILING];
      }
    },

    setMailingRecipients: (state, action: PayloadAction<MailDelivery[]>) => {
      state.mailingRecipients = action.payload;
    },

    addMailingRecipient: (
      state,
      action: PayloadAction<{
        mailProvider: MailDelivery['provider'];
      }>
    ) => {
      if (state.mailingRecipients.length >= MAX_MAIL_ADDRESSES) {
        return;
      }

      const mailToAdd: MailDelivery = {
        ...DEFAULT_MAILING,
        provider: action.payload.mailProvider
      };

      state.mailingRecipients.push(mailToAdd);
    },

    updateMailingRecipientName: (
      state,
      action: PayloadAction<{ newValue: string; index: number }>
    ) => {
      const recipient = state.mailingRecipients[action.payload.index];
      recipient.isNoticeTypeDefault = false;
      recipient.name = action.payload.newValue;
    },

    updateMailingRecipientCopies: (
      state,
      action: PayloadAction<{ newValue: number; index: number }>
    ) => {
      const recipient = state.mailingRecipients[action.payload.index];
      recipient.isNoticeTypeDefault = false;
      recipient.copies = action.payload.newValue;
    },

    updateMailingRecipientAddress: (
      state,
      action: PayloadAction<{ update: Partial<EAddress>; index: number }>
    ) => {
      const recipient = state.mailingRecipients[action.payload.index];
      recipient.isNoticeTypeDefault = false;
      recipient.address = {
        ...recipient.address,
        ...action.payload.update
      };
    },

    deleteMailingRecipient: (state, action: PayloadAction<number>) => {
      state.mailingRecipients.splice(action.payload, 1);
      state.recipientValidationErrors.splice(action.payload, 1);
    },

    setRecipientValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.recipientValidationErrors = action.payload;
    },

    validateMailingRecipients: state => {
      state.recipientValidationErrors = state.mailingRecipients.map(r =>
        hasPopulatedAddress(r) ? getRecipientValidation(r) : ''
      );
    },

    setSendAffidavitToCourthouse: (state, action: PayloadAction<boolean>) => {
      state.sendAffidavitToCourthouse = action.payload;
      if (action.payload === false) {
        state.courthouseRecipients = [DEFAULT_COURTHOUSE];
      }
    },

    setCourthouseRecipients: (state, action: PayloadAction<MailDelivery[]>) => {
      state.courthouseRecipients = action.payload;
    },

    addCourthouseRecipient: state => {
      if (state.courthouseRecipients.length >= MAX_COURTHOUSES) {
        return;
      }
      state.courthouseRecipients.push(DEFAULT_COURTHOUSE);
    },

    selectCourthouseRecipient: (
      state,
      action: PayloadAction<{
        index: number;
        selectedCourthouse: CourtHouse;
        addressState: number;
      }>
    ) => {
      const { selectedCourthouse, index, addressState } = action.payload;
      const recipient = state.courthouseRecipients[index];
      recipient.courtHouse = selectedCourthouse.id;
      recipient.name = selectedCourthouse.name;
      recipient.address = {
        address_line1: selectedCourthouse.address,
        address_line2: '',
        address_state: addressState,
        address_zip: selectedCourthouse.zip,
        address_city: selectedCourthouse.city
      };
    },

    updateCourthouseRecipientCopies: (
      state,
      action: PayloadAction<{ newValue: number; index: number }>
    ) => {
      state.courthouseRecipients[action.payload.index].copies =
        action.payload.newValue;
    },

    deleteCourthouse: (state, action: PayloadAction<number>) => {
      state.courthouseRecipients.splice(action.payload, 1);
      state.courthouseValidationErrors.splice(action.payload, 1);
    },

    setCourthouseValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.courthouseValidationErrors = action.payload;
    },

    validateCourthouseRecipients: state => {
      state.courthouseValidationErrors = state.courthouseRecipients.map(
        getCourthouseValidation
      );
    },

    setShowErrors: (state, action: PayloadAction<boolean>) => {
      state.showErrors = action.payload;
    },

    // Add-on reducers (flag-gated path)
    setAffidavitType: (state, action: PayloadAction<AffidavitType>) => {
      state.affidavitType = action.payload;
    },

    setShippingSpeed: (state, action: PayloadAction<ShippingSpeed>) => {
      state.shippingSpeed = action.payload;
    },

    setDeliveryOption: (state, action: PayloadAction<'email' | 'mail'>) => {
      state.deliveryOption = action.payload;
    },

    setWantsAdditionalAffidavit: (state, action: PayloadAction<boolean>) => {
      state.wantsAdditionalAffidavit = action.payload;
    },

    // Publisher-owned, so the loader is the only writer.
    setNoticeTypeDefaultRecipients: (
      state,
      action: PayloadAction<MailDelivery[]>
    ) => {
      state.noticeTypeDefaultRecipients = action.payload;
    },

    // Clears the paid add-on opt-in and its derived selections. Dispatched on a
    // placement context change (publisher / notice-type / filer) so a stale "yes"
    // can't prefill an address and charge the new publisher; edit prefill
    // re-derives these from saved data afterward.
    resetAffidavitAddOnSelections: state => {
      state.affidavitType = null;
      state.shippingSpeed = 'ground';
      state.deliveryOption = 'email';
      state.wantsAdditionalAffidavit = false;
    },

    setInvoiceMailingRecipients: (
      state,
      action: PayloadAction<MailDelivery[]>
    ) => {
      state.invoiceMailingRecipients = action.payload;
    },

    addInvoiceMailingRecipient: state => {
      if (state.invoiceMailingRecipients.length >= MAX_MAIL_ADDRESSES) {
        return;
      }
      state.invoiceMailingRecipients.push({
        ...DEFAULT_MAILING,
        documentType: MailedNoticeDocumentType.INVOICE,
        shippingTier: ShippingTier.GROUND
      });
    },

    updateInvoiceMailingRecipient: (
      state,
      action: PayloadAction<{ index: number; update: Partial<MailDelivery> }>
    ) => {
      const { index, update } = action.payload;
      const recipient = state.invoiceMailingRecipients[index];
      if (recipient) {
        Object.assign(recipient, update);
      }
    },

    deleteInvoiceMailingRecipient: (state, action: PayloadAction<number>) => {
      state.invoiceMailingRecipients.splice(action.payload, 1);
      state.invoiceRecipientValidationErrors.splice(action.payload, 1);
    },

    setInvoiceRecipientValidationErrors: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.invoiceRecipientValidationErrors = action.payload;
    },

    validateInvoiceMailingRecipients: state => {
      state.invoiceRecipientValidationErrors =
        state.invoiceMailingRecipients.map(r =>
          hasPopulatedAddress(r) ? getRecipientValidation(r) : ''
        );
    }
  }
});
